import React, { Component } from 'react'
import Lottie from 'react-lottie'
import { observable } from 'mobx'
import { observer } from 'mobx-react'
import * as fx from 'glfx-es6'
import * as FileSaver from "file-saver"
import WebGLCanvas from 'broadway-player/Player/YUVCanvas'
import toWav from 'audiobuffer-to-wav'
import Store, { RESUME_ENCODER_EVENT } from './store'
import Tracking from './Tracking';
import VideoEncoder from './lib/VideoEncoder';
import {
  Loader,
  Hidden,
} from './styled';
import { Col, Row, Card, CardBody, CardHeader } from 'reactstrap';
import RenderDialog from './components/RenderDialog';
import Editor from './components/Editor';
import VideoLoadingIndicator from './components/VideoLoadingIndicator';
import LoadingMessage from './components/LoadingMessage';
import { RENDER_SCALE, DEMO_TEXT_ELEMENTS_LINES, TEXT_ELEMENT_LINE_LENGTH } from './constants';
import { SSL_OP_SSLEAY_080_CLIENT_DH_BUG } from 'constants';


const store = new Store()

const isElementContact = (element) => {
  const layerName = element.data.nm;
  return layerName.toLowerCase().indexOf('contact') > -1;
}

const isElementCallToAction = (element) => {
  const layerName = element.data.nm;
  return layerName.toLowerCase().indexOf('call to action') > -1;
}

const ieElementContactOrCallTwoAction = (element) => {
  return isElementCallToAction(element) || isElementContact(element);
}

const isElementFirst = (element) => {
  const layerName = element.data.nm;
  return layerName.toLowerCase().indexOf('first') > -1;
}

const getTextElementId = (element) => {
  if (isElementContact(element)) {
    return 'contact';
  }

  if (isElementCallToAction(element)) {
    return 'c2a';
  }

  if (isElementFirst(element)) {
    return 'first';
  }

  return 'second';
}

@observer
class App extends Component {
  animation = null
  animationData = null
  canvas = null
  screenShot = React.createRef()
  fxCanvas = null
  texture = null

  timeBase = null
  timeVideo = null

  @observable upperLeft = null
  @observable upperRight = null
  @observable lowerLeft = null
  @observable lowerRight = null

  @observable isRendering = false

  @observable currentFrame = null
  encodedFrame = null

  renderScale = RENDER_SCALE
  @observable videoCanvasW = 0;
  @observable videoCanvasH = 0;

  tracking = new Tracking({ ua: 'UA-141547106-3', isEnabled: this.props.isTrackingEnabled })

  componentWillMount() {
    try {
      this.fxCanvas = fx.canvas()
    } catch (e) {
      console.error(e)
    }

    window.addEventListener('resize', this.onWindowResize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onWindowResize);
  }

  onWindowResize = () => {
    const el = document.querySelector('#video-canvas');
    if (el) {
      const { width, height } = el.getClientRects()[0];

      this.videoCanvasW = width;
      this.videoCanvasH = height
    }
  }

  @observable isVideoEncoderLoading = false
  handleVideoEncoderLoaded = () => {
    const {
      animationWidth,
      animationHeight,
      numberOfAudioChannels,
      audioSampleRate,
    } = store;

    const videoConfig = {
      fps: 25,
      width: (animationWidth / this.renderScale) >> 0,
      height: (animationHeight / this.renderScale) >> 0,
      duration: 60,
      bitrate: (6000000 / this.renderScale / this.renderScale) >> 0,
      presetIdx: 0,
    };

    const audioConfig = {
      channels: numberOfAudioChannels,
      samplerate: audioSampleRate,
      bitrate: 128000,
    };

    this.videoEncoder.init(videoConfig, audioConfig, this.handleVideoEncoderReady, this.getFrame)
  }

  handleVideoEncoderReady = () => {
    this.isVideoEncoderLoading = false
  }

  isProcessingFrames = false
  timeLastEncodedFrameDrawn = null

  handleResumeEncoder = () => {
    const batchSize = 50

    if (this.isVideoEncoderLoading || this.isProcessingFrames || this.videoEncoder.frames.length > 50) {
      return
    }

    this.isProcessingFrames = true

    const interval = setInterval(() => {
      if (!this.isRendering || !this.videoEncoder || this.encodedFrame >= this.totalFrames) {
        this.isProcessingFrames = false
        return clearInterval(interval)
      }

      if (!store.videoFrameCache[this.encodedFrame + 1]) {
        void store.getNextVideoFrames(batchSize)
        this.isProcessingFrames = false
        return clearInterval(interval)
      }

      const lookAhead = Math.min(this.totalFrames - 1, this.encodedFrame + batchSize - 1)
      if (!store.videoFrameCache[lookAhead]) {
        void store.getNextVideoFrames(batchSize)
      }

      if (store.canEdit && Date.now() - this.timeLastEncodedFrameDrawn > 500) {
        if (this.canvas) {
          this.timeLastEncodedFrameDrawn = Date.now()
          this.canvas.getContext('2d').drawImage(this.canvasForRender, 0, 0, this.canvas.width, this.canvas.height)
        }
      }

      this.encodedFrame++
      this.encodeFrame()

      delete store.videoFrameCache[this.encodedFrame]
    }, 0)

  }

  textChanged = true
  textElements = null

  textElementsLinesConfig = DEMO_TEXT_ELEMENTS_LINES;

  handleAnimationRef = ref => {
    this.animation = ref
  }

  animationForRender = null
  handleAnimationForRenderRef = ref => {
    this.animationForRender = ref
  }


  @observable totalFrames = null
  framesWithContent = null

  initTextElements() {
    const { anim } = this.animation

    if (!this.framesWithContent) {
      this.framesWithContent = new Array(anim.totalFrames)

      anim
        .renderer
        .elements
        .filter(element => element)
        .forEach(({ data: { ip, op } }) => {
          for (let frame = ip; frame <= op; frame++) {
            this.framesWithContent[frame] = true
          }
        })
    }

    if (!this.textElements || !this.textElements.length || this.textElements[0].comp.animationItem.animationData !== store.animationData) {
      this.totalFrames = anim.totalFrames
      if (!this.textElements) {
        void this.initVideoForRender()
      }

      const textElements = anim
        .renderer
        .elements
        .filter(element => element)
        .filter(element => element.constructor.name === 'CVTextElement')
        .filter(({ data }) => !/^_/.test(data.nm))

      if (!textElements.length) {
        return
      }

      this.textElements = textElements

      this.loadTextElementsValuesFromLocalStorage();

      this.settextElementsLinesConfigBasedOnTextElements();
    }
  }

  settextElementsLinesConfigBasedOnTextElements = () => {
    this.textElementsLinesConfig = this.textElements.map(element => {
      let lines = [];

      const numOfLines = isElementContact(element) ? 3 : 2;
      // const string = element.textProperty.data.d.k[0].s.t;
      // const numOfLinesBasedOnText = Math.ceil(string.length / TEXT_ELEMENT_LINE_LENGTH);

      for (let i = 0; i < numOfLines; i += 1) {
        lines.push(TEXT_ELEMENT_LINE_LENGTH);
      }

      return lines;
    });
  }

  loadTextElementsValuesFromLocalStorage = () => {
    this.textElements.forEach(element => {
      const label = element.data.nm
      const storedValue = window.localStorage.getItem(label)

      if (storedValue) {
        element.updateDocumentData({ t: storedValue })
      }
    })
  }

  handleAnimationFrame = () => {
    if (this.animation) {
      this.initTextElements()
      if (!this.videoForRender || this.videoForRender.paused) {
        this.animation.anim.pause()
      }
    }
  }

  @observable isPlaying = false

  videoForRender = null

  canvasForFrames = null
  handleVideoForRenderDidLoad = () => {
//    URL.revokeObjectURL(this.videoForRender.src)

    const {anim} = this.animation
    this.totalFrames = Math.max(anim.totalFrames, (this.videoForRender.duration * anim.frameRate) >> 0)

    this.initCanvas()
    this.renderFrame()
  }

  canvasForRender = null
  initCanvas() {
    const sourceWidth = this.isRendering
      ? store.animationWidth
      : store.animationWidth / 6
    const sourceHeight = this.isRendering
      ? store.animationHeight
      : store.animationHeight / 6
    const destWidth = this.isRendering
      ? store.animationWidth / this.renderScale
      : store.animationWidth / 3
    const destHeight = this.isRendering
      ? store.animationHeight / this.renderScale
      : store.animationHeight / 3

    if (!this.canvasForFrames) {
      this.canvasForFrames = document.createElement('canvas')
    }
    this.canvasForFrames.width = sourceWidth
    this.canvasForFrames.height = sourceHeight
    if (this.isRendering) {
      if (!this.canvasForRender) {
        this.canvasForRender = new window.OffscreenCanvas(destWidth, destHeight)
      } else {
        this.canvasForRender.width = destWidth
        this.canvasForRender.height = destHeight
      }
    } else {
      this.canvasForRender = null
    }

    this.webGLCanvas = new WebGLCanvas({
      canvas: this.canvasForFrames,
      width: this.canvasForFrames.width,
      height: this.canvasForFrames.height,
    })
  }

  audioContainer = React.createRef()
  seekInterval = null
  lastTimeSeeked = null

  async initVideoForRender() {
    if (this.videoForRender) {
      return
    }

    void await store.loadSoundtrack()

//    const video = document.createElement('video')
    const audio = document.createElement('audio')
    this.videoForRender = audio

    const wav = toWav(store.deocdedAudioBuffer)

    audio.preload = 'auto'
    audio.ontimeupdate = this.handleCurrentFrameChange
//    audio.onseeked = this.handleCurrentFrameChange
    audio.onplay = this.handleVideoPlay
    audio.onpause = this.handleVideoPause
    audio.onloadeddata = this.handleVideoForRenderDidLoad
    audio.crossOrigin = 'anonymous'
//    video.src = URL.createObjectURL(videoBlob)
    audio.src = URL.createObjectURL(new Blob([wav]))
    audio.controls = true
    audio.controlsList = 'nodownload'
    if (store.canEdit) {
      this.audioContainer.current.appendChild(audio)
    }
/*
    this.seekInterval = setInterval(() => {
      if (this.videoForRender.paused) {
        if (this.lastTimeSeeked !== this.videoForRender.currentTime) {
          this.lastTimeSeeked = this.videoForRender.currentTime
          this.handleCurrentFrameChange()
        }
      }
    }, 100)
*/
    audio.currentTime = 0
  }

  renderFrame = (canvas = this.canvas) => {
    if (!this.canvas) {
      return
    }

    const context = canvas.getContext('2d')
    const { anim } = this.isRendering ? this.animationForRender : this.animation
    const { frameRate } = anim
    const currentTime = this.videoForRender.paused
      ? this.videoForRender.currentTime
      : this.videoTimePlayStarted + (Date.now() - this.systemTimePlayStarted) / 1000
    let currentRawFrame = this.isRendering
      ? this.encodedFrame
      : (currentTime * frameRate) >> 0

//    context.imageSmoothingQuality = this.video.paused ? 'high' : 'low'
    context.imageSmoothingQuality = 'low'
//    console.log('renderFrame', currentRawFrame)
    if (!this.isRendering && !this.videoForRender.seeking) {
      this.currentFrame = currentRawFrame
    }

    const cache = this.isRendering ? store.videoFrameCache : store.previewFrameCache
    const cacheFrame = Math.min(store.previewTotalFrames, currentRawFrame)
    if (cache[cacheFrame]) {
      const width = this.isRendering ? store.videoFrameWidth : store.previewFrameWidth
      const height = this.isRendering ? store.videoFrameHeight : store.previewFrameHeight
      const ylen = width * height
      const uvlen = (width >> 1) * (height >> 1)
      const data = cache[cacheFrame]

      this.webGLCanvas.drawNextOutputPicture({
        yData: data.subarray(0, ylen),
        uData: data.subarray(ylen, ylen + uvlen),
        vData: data.subarray(ylen + uvlen, ylen + uvlen + uvlen)
      })
      this.webGLCanvas.contextGL.flush()
      context.drawImage(this.webGLCanvas.canvasElement, 0, 0, canvas.width, canvas.height)
    }

    if (this.isRendering && this.framesWithContent[currentRawFrame]) {
      anim.goToAndStop(currentRawFrame, true)
      const lottieCanvas = document.querySelector('#composition canvas')

      context.drawImage(lottieCanvas, 0, 0, canvas.width, canvas.height)
    }
 
    const frameInNullLayers = (currentRawFrame in store.upperLeft
        && currentRawFrame in store.upperRight
        && currentRawFrame in store.lowerLeft
        && currentRawFrame in store.lowerRight);

    if (frameInNullLayers) {
        const upperLeft = store.upperLeft[currentRawFrame]
        const upperRight = store.upperRight[currentRawFrame]
        const lowerLeft = store.lowerLeft[currentRawFrame]
        const lowerRight = store.lowerRight[currentRawFrame]
        const scale = canvas.width / store.animationWidth
        // const scale = 1
        const textureWidth = store.animationWidth * scale
        const textureHeight = store.animationHeight * scale
        this.fxCanvas
            .draw(this.texture, textureWidth, textureHeight)
            .lensBlur(1, -0.5, 0)
            .perspective(
            [0, 0, textureWidth, 0, 0, textureHeight, textureWidth, textureHeight],
            [upperLeft.x * scale, upperLeft.y * scale,
                upperRight.x * scale, upperRight.y * scale,
                lowerLeft.x * scale, lowerLeft.y * scale,
                lowerRight.x * scale, lowerRight.y * scale],
            )
            .update()

        context.drawImage(this.fxCanvas, 0, 0, canvas.width, canvas.height)
    }
  }

  interval = null

  stopSync = () => {
    clearInterval(this.interval)
    this.interval = null
  }

  systemTimePlayStarted = null
  videoTimePlayStarted = null

  syncVideoFrame = () => {
    if (!this.systemTimePlayStarted) {
      this.systemTimePlayStarted = Date.now()
      this.videoTimePlayStarted = this.videoForRender.currentTime
    }

    if (this.videoForRender.currentTime < this.videoForRender.duration) {
      this.renderFrame()

      if (!this.isPlaying) {
        requestAnimationFrame(this.stopSync)
      }
    } else {
      this.stopSync()
    }
  }


  syncVideo = () => {
    if (this.interval) {
      this.stopSync()
    }

    this.systemTimePlayStarted = null
    this.videoTimePlayStarted = null

    const { anim } = this.animation
    const { frameRate } = anim
    this.interval = setInterval(this.syncVideoFrame, 1000 / frameRate)
    this.syncVideoFrame()
  }

  handleVideoPlay = () => {
    this.isPlaying = true;
    this.animation.anim.goToAndPlay(this.videoForRender.currentTime * 1000);
    this.syncVideo();
    this.tracking.videoPlay(store.compositionName);
  }

  handleVideoPause = event => {
    this.isPlaying = false
    this.animation.anim.pause()
  }

  framePixels = null
  isEncoderIdle = false
  encodeFrame = () => {
    if (!this.isRendering) {
      return
    }

    const { anim } = this.animationForRender

    const audioFrameStart = (this.encodedFrame * store.audioSampleRate / anim.frameRate) >> 0
    const audioFrameEnd = ((this.encodedFrame + 1) * store.audioSampleRate / anim.frameRate) >> 0

    this.videoEncoder.queueFrame({
      type: 'audio',
      left: store.leftChannel.slice(audioFrameStart, audioFrameEnd),
      right: store.rightChannel.slice(audioFrameStart, audioFrameEnd),
      sampleRate: store.audioSampleRate
    })

    this.renderFrame(this.canvasForRender)

    const context = this.canvasForRender.getContext('2d')

    const imageData = context.getImageData(0, 0, this.canvasForRender.width, this.canvasForRender.height)
    const w = imageData.width, h = imageData.height
    const data = imageData.data
    Array.from({length: h}, (val, i) => data.slice(i * w * 4, (i + 1) * w * 4))
      .forEach((val, i) => data.set(val, (h - i - 1) * w * 4))

    this.framePixels = new Uint8Array(this.canvasForRender.width * this.canvasForRender.height * 4)
    this.framePixels.set(data)

    this.videoEncoder.queueFrame({ type: 'video', pixels: this.framePixels })

    if (this.isEncoderIdle) {
      this.isEncoderIdle = false
      this.videoEncoder.sendFrame()
    }
  }

  handleVideoEncoderDone = blob => {
    FileSaver.saveAs(new Blob([blob], { type: 'video/mp4' }), "vid.mp4")
    this.videoEncoder = null
    this.initCanvas()

    this.isRendering = false
    window.location.reload()
  }

  handleVideoRenderAbortDone = () => {
    this.videoEncoder = null
    this.forceUpdate()
  }

  getFrame = () => {
//    console.debug('getFrame', 'Frames in queue', this.videoEncoder.frames.length)

    if (!this.isRendering) {
      this.videoEncoder.close(this.handleVideoRenderAbortDone)
    } else if (this.videoEncoder.frames.length === 0) {
      this.isEncoderIdle = true

      const totalFrames = store.videoTotalFrames || this.totalFrames
      if (this.encodedFrame >= totalFrames - 1) {
        this.videoEncoder.close(this.handleVideoEncoderDone)
      } else {
        this.handleResumeEncoder()
      }
    }
  }

  handleScreenShotChange = (canvas, userInteraction) => {
    store.screenShotUrl = canvas.toDataURL()

    if (userInteraction && store.upperLeft) {
      const frames = Object.keys(store.upperLeft)
      const middleFrame = parseInt(frames[(frames.length / 2) >> 0], 10)

      if (this.videoForRender) {
        this.videoForRender.currentTime = middleFrame / this.animation.anim.frameRate
        this.videoForRender.pause()
      }
    }
  }

  handleAssetChange = (layer, asset, canvas, userInteraction) => {
    const isScreenShot = /screenshot/i.test(layer.nm);
    const context = canvas.getContext('2d')
    const imageData = context.getImageData(0, 0, canvas.width, canvas.height)

    if (!isScreenShot) {
      for (let i = 0; i < imageData.data.length; i += 4) {
        const r = imageData.data[i + 0]
        const g = imageData.data[i + 1]
        const b = imageData.data[i + 2]
        const a = imageData.data[i + 3]

        if (r === 255 && g === 255 && b === 255 && a === 255) {
          imageData.data[i + 3] = SSL_OP_SSLEAY_080_CLIENT_DH_BUG;
        }
      }

      context.putImageData(imageData, 0, 0);
    }

    asset.p = canvas.toDataURL()

    if (isScreenShot) {
      store.screenShotUrl = asset.p

      if (userInteraction) {
        const handleImageReplace = () => {
          this.screenShot.current.removeEventListener('load', handleImageReplace);
          if (store.upperLeft) {
            const frames = Object.keys(store.upperLeft)
            const middleFrame = parseInt(frames[(frames.length / 2) >> 0], 10)

            if (this.videoForRender) {
              this.videoForRender.currentTime = middleFrame / this.animation.anim.frameRate
              this.videoForRender.pause()
              this.syncAnimation()
            }
          }
        }

        this.screenShot.current.addEventListener('load', handleImageReplace)
      }

      this.screenShot.current.src = store.screenShotUrl
    } else {
      store.animationData = Object.assign({}, store.animationData)

      if (userInteraction) {
        if (isScreenShot) {
          this.tracking.changeScreenshot()
        } else {
          this.tracking.changeLogo();
        }
    
        setTimeout(() => {
          const middleFrame = (layer.ip + layer.op) / 2

          if (this.videoForRender) {
            this.videoForRender.currentTime = middleFrame / this.animation.anim.frameRate;
            this.videoForRender.pause();
            this.syncVideo();
          }
        }, 500);
      }

      this.forceUpdate()
    }
  }

  handlePlayClick = () => {
    this.videoForRender.play()
  }

  handlePauseClick = () => {
    this.videoForRender.pause()
  }

  wasVideoPlayingBeforeSeeking = false
  restoreVideoPlayingTimeout = null

  restoreVideoPlaying = () => {
    this.restoreVideoPlayingTimeout = null
    this.wasVideoPlayingBeforeSeeking = false
    this.videoForRender.play()
  }

  handleCurrentFrameChange = (event, currentFrame) => {
    /*
    if (!this.videoForRender.seeking && !this.videoForRender.paused) {
      this.wasVideoPlayingBeforeSeeking = true
      this.videoForRender.pause()
    }
    if (this.wasVideoPlayingBeforeSeeking) {
      clearTimeout(this.restoreVideoPlayingTimeout)
      this.restoreVideoPlayingTimeout = setTimeout(this.restoreVideoPlaying, 500)
    }
    this.currentFrame = currentFrame
    this.videoForRender.currentTime = this.currentFrame / this.animation.anim.frameRate
    */
    const { anim } = this.animation
    if (this.videoForRender.paused) {
      anim.goToAndStop(this.videoForRender.currentTime * 1000)
      this.renderFrame()
    } else {
//      anim.goToAndPlay(this.videoForRender.currentTime * 1000)
    }
  }

  syncAnimation = () => {
//    this.animation.anim.renderer.renderConfig.clearCanvas = false
//    const lottieCanvas = document.querySelector('#composition canvas')
//    lottieCanvas.getContext('2d').clearRect(0, 0, lottieCanvas.width, lottieCanvas.height)
//    this.canvas.getContext('2d').clearRect(0, 0, this.canvas.width, this.canvas.height)
    this.animation.anim.goToAndStop(0)
    this.animation.anim.goToAndStop(this.videoForRender.currentTime * 1000)
    this.renderFrame()
//    this.animation.anim.renderer.renderConfig.clearCanvas = true
  }

  fixEdges(image) {
    const canvas = new window.OffscreenCanvas(image.naturalWidth, image.naturalHeight)
    const context = canvas.getContext('2d')

    context.drawImage(image, 0, 0)
    const pixels = context.getImageData(0 , 0, canvas.width, canvas.height)
    const pitch = pixels.width << 2
    const edge = pixels.height / 100

    for (let e = 0; e < edge; e++) {
      const top = e * pitch
      const bottom = (pixels.height - e - 1) * pitch
      const alpha = 0x7f + ((e + 1) * 0x80 / edge) >> 0
      const e4 = e << 2
      const xEnd = pitch - e4

      for (let x = e4 + 3; x < xEnd; x += 4) {
        pixels.data[x + top] = alpha
        pixels.data[x + bottom] = alpha
      }

      const yEnd = pixels.data.length - pitch * e
      const left = e4 + 3
      const right = ((pixels.width - e - 1) << 2) + 3

      for (let y = e * pitch; y < yEnd; y += pitch) {
        pixels.data[y + left] = alpha
        pixels.data[y + right] = alpha
      }
    }

    context.putImageData(pixels, 0, 0)

    return canvas
  }

  handleScreenShotReady = () => {
    const image = this.fixEdges(this.screenShot.current);
    if (!this.texture) {
      this.texture = this.fxCanvas.texture(image)
    } else {
      this.texture.loadContentsOf(image)
    }
  }

  webGLCanvas = null
  handleCanvasRef = ref => {
    this.canvas = ref

    if (ref && this.webGLCanvas) {
      requestAnimationFrame(() => {
        this.syncVideo()
        this.renderFrame()
      })
    }
  }

  onDownloadClick = () => {
    const { compositionName } = store;
    this.tracking.downloadVideoClick(compositionName);
    this.handleRender();
  }
 
  handleRender1 = () => this.handleRender(1)

  handleRender2 = () => {
    this.handleRender(2)
  }

  handleRender = (scale) => {
    // this.renderScale = scale
    this.isRendering = true
    if (this.videoForRender) {
      this.videoForRender.pause()
    }

    this.encodedFrame = -1
    this.initCanvas()
    store.events.addEventListener(RESUME_ENCODER_EVENT, this.handleResumeEncoder)
    void store.getNextVideoFrames(100)

    if (!this.videoEncoder) {
      this.isVideoEncoderLoading = true
      this.videoEncoder = new VideoEncoder(this.handleVideoEncoderLoaded)
    } else {
      this.handleVideoEncoderLoaded()
    }
  }

  handleRenderAbort = () => {
    this.isRendering = false
    this.initCanvas()
    window.location.reload()
  }

  lottieEventListeners = [
    {
      eventName: 'enterFrame',
      callback: this.handleAnimationFrame,
    }
  ]

  handleGetEncoderProgress = () => {
    if (!this.totalFrames) {
      return 0
    }

    let result = this.encodedFrame / this.totalFrames
    if (result > 0.95) {
      result -= this.videoEncoder.frames.length * 0.01
    }

    return result
  }

  onTextInputFocus = (element) => {
    const { currentRawFrame, frameRate } = this.animation.anim;

    this.tracking.textFocused(getTextElementId(element));
    if (this.videoForRender) {
      if (currentRawFrame < element.data.ip
        || currentRawFrame > element.data.op) {
        this.videoForRender.currentTime = (element.data.ip + element.data.op) / 2 / frameRate
      }
      this.videoForRender.pause()
      this.syncAnimation()
    }
  }

  onTextInputBlur= (element) => {
    this.tracking.textBlur(getTextElementId(element));
  }

  onTextInputChange = (value, element, linesChange) => {
    this.tracking.textEdited(getTextElementId(element))
    const translate = (x,y,z) => element.dynamicProperties[0].v.translate(x, y, z);
    const lineHeight = 50;

    if (!isElementContact(element) && !isElementCallToAction(element)) {
      translate(0, -lineHeight * linesChange, 0);
    }


    this.saveTextElementValueToLocalStorage(element, value);
    element.updateDocumentData({ t: value })
    this.syncAnimation()
  }

  saveTextElementValueToLocalStorage = (element, value) => {
    const label = element.data.nm
    window.localStorage.setItem(label, value)
  }

  onGetThisVideoButtonClick = () => {
    const { productLink, compositionName } = store;
    this.tracking.getVideoClick(compositionName);
    window.top.location.href = productLink;
  }

  isGetVideoButtonDisabled = () => {
    const { textElements } = this;
    if (!textElements) { 
      return true;
    }
    
    return textElements.filter((element) => {
      if (!ieElementContactOrCallTwoAction(element)) {
        return element.textProperty.currentData.t.length < 3;
      };

      return false;
    }).length > 0;
  }

  render() {
    const {
      canEdit,
      canDownload,
      isFirstFrameCached,
      screenShotUrl,
      isFrameCacheReady,
      previewVideoTotalBytes,
      cacheProgress,
      previewVideoLoadedBytes,
      animationHeight,
      layersForAssets,
      animationData,
      error,
      animationWidth,
    } = store;

    if (error) {
      return (
        <Loader>
          <h2>Error: {error}</h2>
        </Loader>
      )
    }

    // const scale = this.isRendering ? this.renderScale : 3
    const width = this.isRendering ? (animationWidth / this.renderScale) >> 0 : Math.min(640, window.innerWidth)
    const height = (width * animationHeight / animationWidth) >> 0

    if (!animationData) {
      return (
        <Loader>
          <h1><LoadingMessage>Loading...</LoadingMessage></h1>
        </Loader>
      )
    }

    // const points = animationHeight / RENDER_SCALE;
    const previewPerc = (previewVideoLoadedBytes * 100 / previewVideoTotalBytes);
    const downloadedMb = (previewVideoLoadedBytes / 1000000).toFixed(1);
    const loadingPerc = cacheProgress * 0.5 + previewPerc * 0.5;

    return (
      <div className="h-100">
        <RenderDialog
          isRendering={this.isRendering}
          isVideoEncoderLoading={this.isVideoEncoderLoading}
          canvasForRender={this.canvasForRender}
          videoEncoder={this.videoEncoder}
          animationForRender={this.animationForRender}
          handleGetEncoderProgress={this.handleGetEncoderProgress}
          handleRenderAbort={this.handleRenderAbort}
        />
        <Hidden id="composition">
          {
            this.isRendering &&
            <Lottie
              width={this.videoCanvasW || width}
              height={this.videoCanvasH || height}
              ref={this.handleAnimationForRenderRef}
              options={{
                animationData: store.animationData,
                renderer: 'canvas',
              }}
            />
          }
          <img
            ref={this.screenShot}
            src={screenShotUrl}
            onLoad={this.handleScreenShotReady}
            alt="screenshot"
          />
        </Hidden>
        <Row noGutters className="h-100 flex-column-reverse flex-md-row">
        {
            canEdit &&
            <Col md="6" className="bg-light p-5 border-right h-100 overflow-auto">
              <Editor
                textElements={this.textElements}
                textElementsLinesConfig={this.textElementsLinesConfig}
                layersForAssets={layersForAssets}
                onTextInputFocus={this.onTextInputFocus}
                onTextInputChange={this.onTextInputChange}
                onTextInputBlur={this.onTextInputBlur}
                handleAssetChange={this.handleAssetChange}
              />
            </Col>
          }
          <Col md={{ size: 6 , offset: canEdit ? 0 : 3 }} className="p-5">
            <Card>
              <CardHeader>Preview</CardHeader>
              <CardBody>
                <div className="video-preview">
                  {
                    isFirstFrameCached &&
                    <canvas
                      className="video-canvas"
                      id="video-canvas"
                      ref={this.handleCanvasRef}
                      width={640}
                      height={360}
                    />
                  }
                  {
                    !this.isRendering && height && 
                    <div
                    className="video-animation-overlay"
                    >
                      <Lottie
                        width={this.videoCanvasW || width}
                        height={this.videoCanvasH || height}
                        ref={this.handleAnimationRef}
                        options={{
                          animationData,
                          // preserveAspectRatio: 'xMidYMid meet'
                          renderer: 'canvas',
                        }}
                        eventListeners={this.lottieEventListeners}
                      />
                    </div>
                  }
                </div>
                <div className="text-center mb-2 mt-2">
                  <div
                    ref={this.audioContainer}
                    className="audio-container"
                    style={{
                      opacity: isFrameCacheReady ? 1 : 0.5,
                    }}
                  />
                  <VideoLoadingIndicator
                    isFrameCacheReady={isFrameCacheReady}
                    cacheProgress={cacheProgress}
                    previewVideoLoadedBytes={previewVideoLoadedBytes}
                    previewVideoTotalBytes={previewVideoTotalBytes}
                    downloadedMb={downloadedMb}
                    loadingPerc={loadingPerc}
                  />
                </div>
                {
                  canDownload &&
                  <button
                    disabled={this.isRendering || this.videoEncoder || !isFrameCacheReady}
                    onClick={this.onDownloadClick}
                    className="btn btn-pink btn-block mt-2"
                  >
                    Download Your Video
                  </button>
                }
                {
                  canEdit && !canDownload &&
                  <button
                    disabled={this.isGetVideoButtonDisabled()}
                    onClick={this.onGetThisVideoButtonClick}
                    className="btn btn-pink btn-block mt-2 font-bold"
                  >
                    <strong>GET YOUR VIDEO FOR ${store.price}</strong>
                  </button>
                }
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    )
  }
}

export default App;



// WEBPACK FOOTER //
// ./src/App.js